// src/component/hooks/useTicketDetail.js
import { useCallback, useEffect, useRef, useState } from 'react';
import useApiService from './useApiService';
import { fetchTicketDetail, postAdminMessage, uploadAttachment } from '../../services/ticketDetail';

export default function useTicketDetail(ticketId, autoFetch = true) {
  const { executeGet, executePost } = useApiService();

  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState('');

  // Keep GET ref stable (not to retrigger effects unnecessarily)
  const getRef = useRef(executeGet);
  useEffect(() => { getRef.current = executeGet; }, [executeGet]);

  const reload = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError('');
    try {
      const d = await fetchTicketDetail(getRef.current, ticketId);
      setDetail(d);
    } catch {
      setError('Ticket yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Initial/auto fetch
  useEffect(() => {
    if (!autoFetch || !ticketId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const d = await fetchTicketDetail(getRef.current, ticketId);
        if (alive) setDetail(d);
      } catch {
        if (alive) setError('Ticket yüklenemedi.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [autoFetch, ticketId]);

  /**
   * Polling: fetch messages every 3s without touching the global "loading" spinner.
   * - Skips when tab is not visible
   * - Skips if a previous poll is still in-flight
   * - Updates state only if there is a new last message (cheap check)
   */
  const pollBusyRef = useRef(false);
  useEffect(() => {
    if (!ticketId) return;
    const interval = setInterval(async () => {
      if (!ticketId) return;
      if (document.hidden) return;          // don't poll in background tabs
      if (pollBusyRef.current) return;      // avoid overlap
      pollBusyRef.current = true;
      try {
        const d = await fetchTicketDetail(getRef.current, ticketId);
        setDetail(prev => {
          if (!prev) return d;
          const prevLastId = prev?.messages?.[prev.messages.length - 1]?.id;
          const nextLastId = d?.messages?.[d.messages.length - 1]?.id;
          // update only if there is a new message (prevents unnecessary re-renders)
          return prevLastId !== nextLastId ? d : prev;
        });
      } catch {
        // swallow polling errors; do not spam UI
      } finally {
        pollBusyRef.current = false;
      }
    },3_000); // 3s
    return () => clearInterval(interval);
  }, [ticketId]);

  /**
   * Send message:
   * - If file provided: upload first (FormData, returns {path}), then send {text, image}
   * - Else: send plain {text}
   * - Returns { ok, code? } so UI can show proper Snackbar text
   */
  const sendMessage = useCallback(async ({ text, file = null }) => {
    if (!ticketId) return { ok: false, code: 'not_found' };
    const assignmentId = detail?.ticket?.assignment_id;
    if (!assignmentId) return { ok: false, code: 'not_found' };

    setSending(true);
    try {
      let imagePath = null;
      if (file) {
        try {
          const form = new FormData();
          form.append('assignment_id', String(assignmentId));
          form.append('ticket_id', String(ticketId));
          form.append('file', file);
          const up = await uploadAttachment(executePost, { assignmentId, ticketId, file }); // POST /api/boost/attachments
          imagePath = up?.path || null;
        } catch (e) {
          const code = e?.response?.data?.error || 'server_error';
          return { ok: false, code };
        }
      }

      // Admin message: POST /api/boost/tickets/:id/messages with { text, image }
      const msg = await postAdminMessage(executePost, ticketId, { text, image: imagePath });

      // Optimistically append sent message
      setDetail(prev => prev
        ? { ...prev, messages: [...(prev.messages || []), msg] }
        : prev
      );

      return { ok: true };
    } catch (e) {
      const code = e?.response?.data?.error || 'server_error';
      return { ok: false, code };
    } finally {
      setSending(false);
    }
  }, [ticketId, detail, executePost]);

  return { detail, loading, error, sending, reload, sendMessage };
}
